import { useEffect, useState } from "react";

/**
 * Сколько пикселей снизу занимает экранная клавиатура. Ноль, когда её нет.
 *
 * ⚠️ **Заведено ради чата занятия** (раздел 12 `docs/room-chat-design.md`): на
 * узком экране карточка приезжает нижним листом и прижата к низу окна, то есть
 * поле ввода оказывается ровно под клавиатурой, и человек печатает вслепую.
 *
 * ⚠️ **Довод против `visualViewport` из раздела 10 проекта сюда НЕ переносится,
 * и это сказано вслух.** В комнате он отвергнут потому, что оба её предка
 * `overflow: hidden`, прокручивать нечего, а `interactive-widget` будит
 * наблюдателя размера доски и способен вернуть панель инструментов внутрь
 * карточки посреди урока. Здесь ничего этого нет: наблюдатель размера у
 * карточки уже работает, и работает он ради самой карточки, а доски на экране
 * нет вовсе.
 *
 * ⚠️ **Считается ОДНОЙ формулой и только вниз.** `window.innerHeight` это
 * раскладочный вьюпорт, который клавиатура на iOS не уменьшает вовсе, а
 * `visualViewport` это видимая часть; разница между ними и есть клавиатура.
 * `offsetTop` в формуле обязателен: при увеличении щипком видимая часть
 * съезжает, и без него страница считала бы клавиатуру там, где её нет.
 * Отрицательные значения отбрасываются: на Android при некоторых панелях
 * видимая часть бывает ВЫШЕ раскладочной, и лист уехал бы за нижний край.
 *
 * ⚠️ Слушается только в положении листа. У боковой карточки `bottom` не задан
 * вовсе (она стоит по `top`), поднимать её нечем, а лишний слушатель на каждое
 * открытие карточки в сетке нам не нужен.
 */
export function useKeyboardInset(sheet: boolean): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!sheet || vv === undefined || vv === null) {
      setInset(0);
      return;
    }
    const sync = () => {
      setInset(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    };
    sync();
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
      setInset(0);
    };
  }, [sheet]);

  return inset;
}
